/**
 * Flat LN2 message bridge shared by the Numato and STM32 CDO editors.
 *
 * The Numato GPIO and Relay editors and the STM32 editor persist each LN2
 * message as the flat quad LiftCloud's decoders consume
 * (`packer.NumatoFlatMessage`, in utils/flatbuffers/binary/numato_pack.go), not
 * as the nested `EncodedMsgContent` union the wizard used to emit. This module
 * is the only place that converts between that flat shape and the composite key
 * ("<category>:<variant>:<value>") the message tables in ./ln2MessageOptions
 * speak.
 *
 * The nested-union path it replaced emitted a one-part category key
 * ("status"), which parseCompositeKey rejects; the builder then fell back to a
 * constant status_message placeholder that carried no enum value, which is why
 * every selection collapsed to the same message. That path no longer exists:
 * this module builds on the lower-level primitives in ./ln2MessageOptions, and
 * both families of editor go through it.
 */
import { LiftType } from "../generated/archive/lift-type";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import {
  LN2_MESSAGE_CATEGORIES,
  decodeFlattenedMessage,
  getCategory,
  getDisplayName,
  messageContentToCategoryKey,
  parseCompositeKey,
  type Ln2MessageOption,
} from "./ln2MessageOptions";

/**
 * The flat projection of one LN2 message, as persisted in Numato cdo_input.
 * Field types follow the BacnetFrontEndMessage precedent in
 * src/interfaces/Adapter.interface.ts:48-61: plain `number` for the two
 * discriminants (so a value read back off the wire needs no enum cast) and the
 * real `LiftType` for the schema branch, which is a closed two-value choice.
 */
export interface NumatoFlatMessage {
  /** Ln2.EncodedMessageContent — 1 OneHotMessage, 2 BitMaskMessage. */
  encoded_type: number;
  /** Ln2.MessageContent — 1..9. 0 (NONE) means "not configured yet". */
  message_content: number;
  /** The category's own enum value, e.g. Status.Power. */
  message_value: number;
  /** Archive.LiftType. Selects the elevator vs escalator schema branch. */
  fbs_device_type: LiftType;
  /** Traffic/Control elevator position slot. null everywhere else. */
  position: number | null;
  /** Ln2.ValuedMessage.active. */
  active: boolean;
}

/**
 * The only two Archive.LiftType values that survive a CDO round-trip.
 *
 * LiftType also defines MOVINGWALK(2), BOT(3) and GPIO(4), but LiftCloud's
 * shared expander collapses every non-zero value into the escalator branch
 * (isElevator, sequent_pack.go:250) and re-emits literal 1 on unpack (elevOrEsc,
 * sequent_unpack.go:275). 2, 3 and 4 therefore come back as 1. That is
 * pre-existing Sequent behaviour this change does not widen, so the control
 * offers only the two values that mean what they say.
 */
export const NUMATO_DEVICE_BRANCHES: readonly { label: string; value: LiftType }[] = [
  { label: "Elevator (0)", value: LiftType.ELEVATOR },
  { label: "Escalator (1)", value: LiftType.ESCALATOR },
];

/** One `<Select.OptGroup>` worth of options, normal variant only. */
export interface NumatoMessageOptionGroup {
  categoryKey: string;
  label: string;
  options: (Ln2MessageOption & { displayName: string })[];
}

/**
 * Grouped picker options — the `normal` variant only.
 *
 * The flat quad has no variant slot, and every builder in LiftCloud's shared
 * expander emits the Normal* variant (sequent_pack.go:262,280,300,318,336,356,
 * 374,395,420). A `valued` option would be silently downgraded on pack, so it
 * is not offered — and numatoFlatMessageFromCompositeKey rejects one outright
 * rather than relying on the dropdown to be the only entry point.
 */
export const NUMATO_MESSAGE_OPTION_GROUPS: NumatoMessageOptionGroup[] = LN2_MESSAGE_CATEGORIES.map(
  (cat) => ({
    categoryKey: cat.categoryKey,
    label: cat.label,
    options: cat.options
      .filter((o) => o.variant === "normal")
      .map((o) => ({ ...o, displayName: getDisplayName(o.key) })),
  }),
);

/**
 * The two MessageContent variants whose elevator branch has a position field:
 * Traffic.ElevatorTrafficMessage and Control.ElevatorControlMessage. Verified
 * against sequent_pack.go:284-289 and :340-345 — no other builder reads
 * msg.Position, and neither escalator branch does.
 */
const POSITION_CARRYING_CONTENT: ReadonlySet<number> = new Set<number>([
  MessageContent.Traffic_TrafficMessage,
  MessageContent.Control_ControlMessage,
]);

/** Whether this message's FlatBuffer branch actually has a position slot. */
export function numatoFlatMessageCarriesPosition(msg: NumatoFlatMessage): boolean {
  return (
    msg.fbs_device_type === LiftType.ELEVATOR && POSITION_CARRYING_CONTENT.has(msg.message_content)
  );
}

/**
 * Force a message into the only shape that round-trips.
 *
 * Two normalizations, both mirroring the backend exactly:
 *   - fbs_device_type collapses to ELEVATOR or ESCALATOR, because isElevator()
 *     tests `== 0` and elevOrEsc() re-emits 1 for everything else.
 *   - position is null unless the branch has the slot, and 0 rather than null
 *     when it does — extractTrafficFlat/extractControlFlat always return a
 *     non-nil pointer on the elevator branch, so a null sent for a Traffic
 *     message would read back as 0 and break an exact JSON round-trip.
 *
 * Returns the input by reference when nothing changed, so React state identity
 * is preserved on a no-op.
 */
export function normalizeNumatoFlatMessage(msg: NumatoFlatMessage): NumatoFlatMessage {
  const branch = msg.fbs_device_type === LiftType.ELEVATOR ? LiftType.ELEVATOR : LiftType.ESCALATOR;
  const carries = numatoFlatMessageCarriesPosition({ ...msg, fbs_device_type: branch });
  const position = carries ? (msg.position ?? 0) : null;

  if (branch === msg.fbs_device_type && position === msg.position) return msg;

  return { ...msg, fbs_device_type: branch, position };
}

/**
 * A freshly added mapping row's message: deliberately unconfigured.
 *
 * message_content 0 (NONE) has no builder in LiftCloud's messageContentBuilders
 * registry, so the server rejects it by name at save time. Rather than paper
 * over that with a silent default the user did not choose, the row starts empty
 * and StepAdaptersCdoEditor's completeness check keeps the save button disabled
 * until every row names a message (see numatoCdoMessagesAreConfigured).
 */
export function buildDefaultNumatoFlatMessage(): NumatoFlatMessage {
  return {
    encoded_type: EncodedMessageContent.NONE,
    message_content: MessageContent.NONE,
    message_value: 0,
    fbs_device_type: LiftType.ELEVATOR,
    position: null,
    active: true,
  };
}

/**
 * Coerce whatever came back from the server (or from a legacy nested-union CDO
 * still in the database) into a well-formed flat message.
 *
 * A pre-contract payload has no numeric message_content, so it lands on NONE
 * and renders as "Not configured" — the honest outcome. Guessing a message from
 * the old nested shape would silently rewrite the operator's configuration.
 */
export function readNumatoFlatMessage(raw: unknown): NumatoFlatMessage {
  const src = (raw ?? {}) as Record<string, unknown>;
  const num = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;

  return normalizeNumatoFlatMessage({
    encoded_type: num(src.encoded_type, EncodedMessageContent.NONE),
    message_content: num(src.message_content, MessageContent.NONE),
    message_value: num(src.message_value, 0),
    fbs_device_type: num(src.fbs_device_type, LiftType.ELEVATOR),
    position: typeof src.position === "number" ? src.position : null,
    active: typeof src.active === "boolean" ? src.active : true,
  });
}

/**
 * Composite key -> flat quad. Returns null for anything unrepresentable: a key
 * that is not three parts, a `valued` variant, or a category that does not
 * exist. `prev` supplies the fields the key says nothing about (device branch,
 * position, active); the result is normalized, so switching from Traffic to
 * Status clears a stale position in the same step.
 */
export function numatoFlatMessageFromCompositeKey(
  compositeKey: string,
  prev: NumatoFlatMessage,
): NumatoFlatMessage | null {
  const parsed = parseCompositeKey(compositeKey);
  if (!parsed) return null;
  if (parsed.variant !== "normal") return null;

  const category = getCategory(compositeKey);
  if (!category) return null;

  return normalizeNumatoFlatMessage({
    ...prev,
    encoded_type: category.encodedMessageContent,
    message_content: category.messageContent,
    message_value: parsed.enumValue,
  });
}

/**
 * Flat quad -> composite key. Exact inverse of the above for every option the
 * picker offers. Null when nothing is configured, or when the stored
 * (message_content, message_value) pair names no option in the current schema —
 * decodeFlattenedMessage does that check, which is why the key is not simply
 * concatenated from its parts.
 */
export function compositeKeyFromNumatoFlatMessage(msg: NumatoFlatMessage): string | null {
  if (msg.message_content === MessageContent.NONE) return null;

  return decodeFlattenedMessage(msg.message_content, msg.message_value)?.key ?? null;
}

/** Human label for the current selection, for a caption beside the picker. */
export function numatoFlatMessageLabel(msg: NumatoFlatMessage): string {
  const exact = compositeKeyFromNumatoFlatMessage(msg);
  if (exact) return getDisplayName(exact);

  const categoryKey = messageContentToCategoryKey(msg.message_content);
  if (!categoryKey) return "Not configured";

  // getDisplayName falls back to "<Category>: <value>" when no option matches,
  // so a stored value outside the current enum still renders informatively
  // instead of vanishing.
  return `${getDisplayName(`${categoryKey}:normal:${msg.message_value}`)} (unrecognized)`;
}

/** True when a raw stored message hydrates to one the picker can name. */
function isConfiguredMessage(raw: unknown): boolean {
  return compositeKeyFromNumatoFlatMessage(readNumatoFlatMessage(raw)) !== null;
}

/**
 * True when every mapping row in a parsed Numato CDO names a real LN2 message.
 *
 * The server rejects message_content 0 with a named error, so an unconfigured
 * row is a 400 at PUT. Every Numato CDO written before this contract lands
 * hydrates to NONE, so without this check the first save of an existing adapter
 * would fail opaquely. Used by StepAdaptersCdoEditor.isCdoValid.
 */
export function numatoCdoMessagesAreConfigured(parsed: unknown): boolean {
  const obj = (parsed ?? {}) as {
    response_to_message?: unknown[];
    message_to_cmd?: unknown[];
  };
  const r2m = Array.isArray(obj.response_to_message) ? obj.response_to_message : [];
  const m2c = Array.isArray(obj.message_to_cmd) ? obj.message_to_cmd : [];

  return (
    r2m.every((e) => isConfiguredMessage((e as { value?: { msg?: unknown } })?.value?.msg)) &&
    m2c.every((e) => isConfiguredMessage((e as { key?: unknown })?.key))
  );
}

/**
 * True when every `msg_to_cmd` row in a parsed STM32 CDO names a real LN2
 * message. Same reasoning as numatoCdoMessagesAreConfigured: the server answers
 * message_content 0 with a 400 at PUT, and a CDO saved before the flat contract
 * hydrates to NONE, so the check keeps the first save of an existing adapter
 * from failing opaquely. A missing or empty `msg_to_cmd` has no rows to name.
 * Used by StepAdaptersCdoEditor.isCdoValid.
 */
export function stm32CdoMessagesAreConfigured(parsed: unknown): boolean {
  const obj = (parsed ?? {}) as { msg_to_cmd?: unknown[] };
  const rows = Array.isArray(obj.msg_to_cmd) ? obj.msg_to_cmd : [];

  return rows.every((e) => isConfiguredMessage((e as { key?: unknown })?.key));
}
